// The listing description, built from the structured intake fields (Shane's format, 4 Nov 2026):
//
//   Description:      one short sentence
//   (Tim, 4 Nov: Color / Material / Tag / Era / Graphics are captured on the form but NOT shown on the website description;
//    they are saved on the product as a hidden metafield, see detailsOf, for filters and SEO.)
//   Condition notes:  only when there is something to say
//   Measurements:     one line per measurement, in inches
//
// Pure TypeScript with no Deno or browser APIs, so the counter (live preview) and the intake-create function (what is written to
// Shopify) share one copy. The website layout is still being settled, so everything that decides the look is in renderHtml and
// renderLines below; the fields and the form do not change when the layout does.
import { siteCategories } from './site-category.ts'

export interface Measure { key: string; label: string; value: string; unit: string }

/** What the intake form collects for the description. Every part is optional; a blank part is simply left out. */
export interface ListingDraft {
  description: string          // the one sentence; blank = built from era, item and graphics
  descriptionEdited: boolean   // true once a person typed in the box, so the auto sentence stops overwriting it
  color: string[]
  material: string[]
  tag: string                  // typed
  era: string
  graphics: string[]
  notes: string                // condition notes, typed
  measures: Record<string, string>   // measure key → number as typed
}

export const emptyListing = (): ListingDraft => ({ description: '', descriptionEdited: false, color: [], material: [], tag: '', era: '', graphics: [], notes: '', measures: {} })

// ---- which parts of the form apply to a piece ------------------------------------------------------------------------------

export type Kind = 'top' | 'outer' | 'bottom' | 'dress' | 'shoes' | 'bag' | 'jewelry' | 'eyewear' | 'belt' | 'hat' | 'other'

const has = (s: string, words: string[]) => words.some((w) => new RegExp(`\\b${w}\\b`, 'i').test(s))

/** The kind of piece, from the category picked on the form and the item name. */
export function kindOf(categoryId: string, item: string): Kind {
  const cats = siteCategories(categoryId)
  if (cats.includes('Jewelry') || cats.includes('Watches') || has(item, ['RING', 'NECKLACE', 'CHAIN', 'PENDANT', 'BRACELET', 'EARRINGS?', 'CHARM'])) return 'jewelry'
  if (cats.some((c) => ['Jackets', 'Vests'].includes(c))) return 'outer'
  if (cats.some((c) => ['T-shirts', 'Shirts', 'Hoodies', 'Sweaters/Sweatshirts', 'Corsets'].includes(c))) return 'top'
  if (cats.some((c) => ['Pants', 'Denim', 'Shorts', 'Skirts'].includes(c))) return 'bottom'
  if (cats.includes('Dresses')) return 'dress'
  if (cats.some((c) => ['Shoes', 'Heels'].includes(c))) return 'shoes'
  if (cats.some((c) => ['Bags', 'Wallets'].includes(c))) return 'bag'
  if (cats.includes('Eyewear')) return 'eyewear'
  if (cats.includes('Belts')) return 'belt'
  if (cats.includes('Hats')) return 'hat'
  return 'other'
}

export const APPAREL: Kind[] = ['top', 'outer', 'bottom', 'dress']
/** Tag (the neck / brand tag) and Graphics only mean something on clothes; Graphics only on things that carry a print. */
export const showTag = (k: Kind) => APPAREL.includes(k)
export const showGraphics = (k: Kind) => k === 'top' || k === 'outer' || k === 'hat'

const LONG_SLEEVE = ['LONG SLEEVE', 'LS', 'HOODIE', 'ZIP UP HOODIE', 'CREWNECK', 'SWEATER', 'SWEATSHIRT', 'THERMAL', 'FLANNEL', 'CARDIGAN', 'PULLOVER', 'SHIRT JACKET']

interface MeasureDef { key: string; label: string; unit: string }
const M = (key: string, label: string, unit = '"'): MeasureDef => ({ key, label, unit })

/** The measurements asked for, by kind of piece (the same sets the listing QA checks: Spec 11). */
export function measureDefs(kind: Kind, item: string, categoryId = ''): MeasureDef[] {
  const up = item.toUpperCase()
  const cats = siteCategories(categoryId)
  switch (kind) {
    case 'top': return [M('p2p', 'Pit to pit'), M('length', 'Length'), ...(has(up, LONG_SLEEVE) ? [M('sleeve', 'Sleeve')] : [])]
    case 'outer': return [M('p2p', 'Pit to pit'), M('length', 'Length'), ...(cats.includes('Vests') ? [] : [M('sleeve', 'Sleeve')])]
    case 'bottom': return cats.includes('Skirts') ? [M('waist', 'Waist'), M('length', 'Length')] : [M('waist', 'Waist'), M('inseam', 'Inseam'), M('thigh', 'Thigh'), M('lo', 'Leg opening')]
    case 'dress': return [M('p2p', 'Pit to pit'), M('length', 'Length')]
    case 'bag': return [M('width', 'Width'), M('height', 'Height'), M('depth', 'Depth')]
    case 'eyewear': return [M('frame', 'Frame width')]
    case 'belt': return [M('total', 'Total length'), M('width', 'Width')]
    case 'jewelry':
      if (has(up, ['RING'])) return [M('ringsize', 'Ring size', ''), M('weight', 'Weight', ' g')]
      if (has(up, ['NECKLACE', 'CHAIN', 'BRACELET'])) return [M('length', 'Length'), M('weight', 'Weight', ' g')]
      return [M('weight', 'Weight', ' g')]
    default: return []
  }
}

/** The measurements that were filled in, in order, as label / value / unit. */
export function measuresOf(l: ListingDraft, kind: Kind, item: string, categoryId = ''): Measure[] {
  return measureDefs(kind, item, categoryId).map((d) => ({ ...d, value: (l.measures[d.key] ?? '').trim() })).filter((m) => m.value)
}

/** The short form that goes on the intake card and the measurements field: "P2P 23 · L 27". */
const SHORT: Record<string, string> = { p2p: 'P2P', length: 'L', sleeve: 'Sl', waist: 'W', inseam: 'In', thigh: 'Th', lo: 'LO', width: 'W', height: 'H', depth: 'D', frame: 'Frame', total: 'Total', ringsize: 'Ring', weight: 'Wt' }
export function measureShort(ms: Measure[]): string {
  return ms.map((m) => `${SHORT[m.key] ?? m.label} ${m.value}${m.unit.trim() === 'g' ? 'g' : ''}`).join(' · ')
}

// ---- the sentence ------------------------------------------------------------------------------------------------------------

const clean = (s: string) => s.replace(/\s+/g, ' ').trim()
const sentenceCase = (s: string) => { const t = clean(s); return t ? t.charAt(0).toUpperCase() + t.slice(1) : '' }
const titleish = (s: string) => clean(s).toLowerCase().replace(/(^|[\s"(-])([a-z])/g, (_m, a: string, b: string) => a + b.toUpperCase())

/** A starting sentence from what is known: "Vintage 1990s Slayer "Undisputed Attitude" tee with faded front and back graphics." */
export function autoSentence(l: ListingDraft, brand: string, item: string): string {
  const it = titleish(item)
  if (!it) return ''
  const era = l.era && /^\d{4}s$/.test(l.era) && Number(l.era.slice(0, 4)) <= 2005 ? `Vintage ${l.era}` : l.era
  const head = [era, titleish(brand), it].filter(Boolean).join(' ')
  const g = l.graphics.map((x) => x.toLowerCase()).filter((x) => x !== 'none')
  const mods = g.filter((x) => x === 'faded' || x === 'cracked'), places = g.filter((x) => x !== 'faded' && x !== 'cracked')
  const gfx = g.length ? ` with ${[...mods, places.join(' and ') || ''].filter(Boolean).join(' ')} graphics` : ''
  return sentenceCase(head) + gfx + '.'
}

export function descriptionOf(l: ListingDraft, brand: string, item: string): string {
  return l.descriptionEdited ? sentenceCase(l.description) : autoSentence(l, brand, item)
}

// ---- rendering ---------------------------------------------------------------------------------------------------------------

export interface Section { heading: string; lines: string[]; prose?: boolean }

/** The sections of the description, headings and lines, with blank parts left out. This is the one place the format is decided. */
export function sections(l: ListingDraft, brand: string, item: string, kind: Kind, categoryId = ''): Section[] {
  const out: Section[] = []
  const desc = descriptionOf(l, brand, item)
  if (desc) out.push({ heading: 'Description:', lines: [desc], prose: true })
  const notes = clean(l.notes)
  if (notes) out.push({ heading: 'Condition notes:', lines: [sentenceCase(notes).replace(/([^.!?])$/, '$1.')], prose: true })
  const ms = measuresOf(l, kind, item, categoryId)
  if (ms.length) out.push({ heading: 'Measurements:', lines: ms.map((m) => `${m.label}: ${m.value}${m.unit}`) })
  return out
}

/** Color, Material, Tag, Era and Graphics as plain lists, blank ones left out. Saved on the product (custom.intake_details), not shown on the site. */
export function detailsOf(l: ListingDraft, kind: Kind): Record<string, string[]> {
  const d: Record<string, string[]> = {}
  const put = (k: string, v: string[]) => { const x = v.map(clean).filter(Boolean); if (x.length) d[k] = x }
  put('color', l.color); put('material', l.material); put('era', [l.era])
  if (showTag(kind)) put('tag', [l.tag])
  if (showGraphics(kind)) put('graphics', l.graphics)
  return d
}

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

/** Shopify descriptionHtml: bold heading, then the lines (one paragraph for prose, line breaks for a list). */
export function renderHtml(secs: Section[]): string {
  return secs.map((s) => `<p><strong>${esc(s.heading)}</strong></p>\n<p>${s.lines.map(esc).join('<br>')}</p>`).join('\n')
}

// ---- the listing title -------------------------------------------------------------------------------------------------------

const SMALL = new Set(['and', 'of', 'the', 'with', 'in', 'on', 'to', 'for', 'a', 'an', 'by', 'at', 'or'])
const KEEP_CAPS = new Set(['ERD', 'CH', 'NWT', 'XL', 'XXL', 'XS', 'LS', 'SS', 'USA', 'NYC', 'OG', 'DJ', 'NY', 'LA', 'UK', 'MA', 'RN', 'II', 'III', 'IV', 'PC', 'TV', 'GV', 'BMX', 'MTV', 'NBA', 'NFL', 'MLB', 'NHL', 'UFO', 'JFK', 'FBI', 'CIA'])
/** Title Case for a listing title (Tim, 4 Nov: titles are Title Case now, not all caps). Known abbreviations (ERD, NWT, XL…) stay capitals. */
export function titleCase(s: string): string {
  return clean(s).split(' ').map((w, i) => {
    const up = w.toUpperCase()
    if (KEEP_CAPS.has(up)) return up
    if (i > 0 && SMALL.has(w.toLowerCase())) return w.toLowerCase()
    return w.toLowerCase().replace(/(^|[-/("])([a-z\u00c0-\u024f])/g, (_m, a: string, b: string) => a + b.toUpperCase())
  }).join(' ')
}

/** "Chrome Hearts" + "VINE DAGGER PENDANT" + "XL" → "Chrome Hearts Vine Dagger Pendant Sz XL". The brand keeps the case it was saved in. */
export function listingTitle(brand: string, item: string, size: string): string {
  const b = clean(brand), sz = clean(size).toUpperCase()
  return [b, titleCase(item)].filter(Boolean).join(' ') + (sz ? ` Sz ${sz}` : '')
}
